import { type RefObject, useEffect } from 'react';

/** How many `useDismiss` overlays are open right now. */
let openOverlays = 0;

/**
 * Is one of this package's hand-rolled overlays open?
 *
 * For a HOST that closes on Escape itself — a Radix dialog wrapping the
 * builder listens on the document in the capture phase, so it hears an Escape
 * before the overlay does, and one press would close both. The host asks this
 * first and leaves that press to the overlay.
 */
export const hasOpenOverlay = (): boolean => openOverlays > 0;

/**
 * Close an overlay on Escape or a pointer press outside it.
 *
 * `pointerdown` rather than `click`, so the overlay is gone before whatever the
 * user pressed on receives its own event — otherwise dismissing feels like it
 * lags a frame behind the thing you actually wanted to press.
 *
 * Lives here rather than in the picker it was born in: the pickers are now the
 * shared `Picker` from `@clockwise/ui`, which gets dismissal from Radix. The one
 * remaining hand-rolled overlay in this package is the filter chip's value
 * popover, which is `position: absolute` inside its own chip and so never had
 * the clipping problem that moved everything else onto Radix.
 */
export function useDismiss(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
  close: () => void,
  /**
   * The TRIGGER that toggles this overlay, if pressing it toggles rather than opens.
   *
   * Closing on `pointerdown` and re-opening on the trigger's `click` are the
   * same physical press, so without this an overlay whose trigger toggles it
   * closes and immediately reopens — and the button appears not to work. The
   * trigger owns that decision; this hook only has to stay out of the way.
   */
  ignore?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    openOverlays += 1;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || ignore?.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      openOverlays -= 1;
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, ref, close, ignore]);
}
